import { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import ServiceImg from "../../assets/images/servicepageimg.png";

// Styled components for styling the layout
const PageWrapper = styled.div`
  padding: 40px 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  background-color: #f8f9fa;
  color: #333;
`;

const SectionWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 1200px;
  align-items: center;

  @media (min-width: 868px) {
    flex-direction: row;
  }
`;

const Number = styled.div`
  font-size: 4rem;
  font-weight: bold;
  position: relative;
  color: rgb(167, 140, 76);
  margin-right: 20px;
  flex-shrink: 0;
  flex: 1.5;
  justify-content: center;
  display: flex;

  @media (min-width: 868px) {
    font-size: 15rem;
  }
`;


const Content = styled.div`
  flex: 2;
`;

const Title = styled.h2`
  font-size: 24px;
  margin-bottom: 10px;
  font-family: "Paris avenue";
  position: relative;
  padding-bottom: 10px;

  &::after {
    content: "";
    position: absolute;
    bottom: 0;
    left: 0;
    width: 0;
    height: 4px;
    background-color: rgb(167, 140, 76);
    transition: width 0.8s ease;
  }

  &.visible::after {
    width: 100%;
  }

  @media (min-width: 868px) {
    font-size: 28px;
  }
`;

const Description = styled.p`
  font-size: 16px;
  line-height: 1.6;
  font-family: "poppins";
  ${"" /* text-align: center; */}
`;

const List = styled.ul`
  font-size: 16px;
  line-height: 1.6;
  padding-left: 20px;
  margin-top: 10px;
  font-family: "poppins";
`;

const ListItem = styled.li`
  margin-bottom: 5px;
  font-family: "poppins";
  font-size: 0.9rem;
`;

const ServiceItems = styled.div`
  width: 80%;
  margin: 0 auto;
`;

const Header = styled.h1`
  font-size: 3rem;
  text-align: center;
  margin: 10rem 0 2rem 0;
  font-family: "Paris avenue";

  @media (max-width: 768px) {
    margin: 4rem 0 0 0;
  }
`;


const Image = styled.img`
  flex: 1;
  width: 100%;
  max-width: 500px;
  border-radius: 10px;
  margin-top: 20px;

  @media (min-width: 868px) {
    margin-top: 0;
    margin-left: 20px;
  }
`;

const HeadSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 50px;
  width: 80%;
  margin: 0 auto;
  @media (max-width: 768px) {
    width: 100%;
  }
  @media (min-width: 868px) {
    flex-direction: row;
    justify-content: space-between;
  }
`;

const HeadTextWrapper = styled.div`
  flex: 1;
  padding: 20px;
`;

const HeadTitle = styled.h2`
  font-size: 2rem;
  margin-bottom: 20px;

  @media (max-width: 768px) {
    margin: 0px;
  }
`;


const HeadSubtitle = styled.h3`
  font-size: 1.5rem;
  color: #000;
  margin-bottom: 20px;

  @media (max-width: 768px) {
    margin-bottom: 0;
  }
`;
const HeadDescription = styled.p`
  font-size: 1.2rem;
  line-height: 1.6;
`;
const WhyUsSection = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 30px;
  justify-content: center;
  margin: 40px 0;
`;
const WhyUsCard = styled.div`
  background-color: ${(props) => (props.highlight ? "rgb(167, 140, 76)" : "#fff")};
  color: ${(props) => (props.highlight ? "#fff" : "#000")};
  padding: 5px 15px;
  border-radius: 10px;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s ease;
  flex: 1 1 calc(33% - 30px);
  max-width: 500px;

  &:hover {
    transform: translateY(-10px);
  }

  @media (max-width: 768px) {
    flex: 1 1 100%;
  }
`;
const CardIcon = styled.div`
  margin-top: 0rem;
  font-size: 2.5rem;
`;
const CardTitle = styled.h3`
  font-size: 1.6rem;
  margin-bottom: 0px;
  margin-top: 0px;
`;
const CardDescription = styled.p`
margin-top: 0px;
  font-size: 1rem;
`;

const AnimatedTitle = ({ children }) => {
  const titleRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true); // Add 'visible' class when in view
        } else {
          setIsVisible(false); // Remove 'visible' class when out of view
        }
      },
      {
        threshold: 0.1, // Adjust threshold as needed
      }
    );

    if (titleRef.current) {
      observer.observe(titleRef.current);
    }

    return () => {
      if (titleRef.current) {
        observer.unobserve(titleRef.current);
      }
    };
  }, []);

  return (
    <Title ref={titleRef} className={isVisible ? "visible" : ""}>
      {children}
    </Title>
  );
};

const ServiceItem = ({ number, title, description, points }) => (
  <SectionWrapper>
    <Number>{number}</Number>
    <Content>
      <AnimatedTitle>{title}</AnimatedTitle>
      <Description>{description}</Description>
      {points && (
        <List>
          {points.map((point, index) => (
            <ListItem key={index}>{point}</ListItem>
          ))}
        </List>
      )}
    </Content>
  </SectionWrapper>
);

const ServicesPage = () => {
  let width = window.innerWidth;
  return (
    <PageWrapper>
      <Header>Services</Header>
      <HeadSection>
        <HeadTextWrapper>
          <HeadTitle>
            <span style={{ fontFamily: "Paris avenue" }}>
              The Embark Properties
            </span>
          </HeadTitle>
          <HeadSubtitle>Take a look at our list of services</HeadSubtitle>
          <HeadDescription>
            At{" "}
            <span style={{ fontFamily: "Paris avenue" }}>
              The Embark Properties
            </span>
            , we provide a comprehensive range of services designed to meet all
            your real estate needs. From finding your dream property to
            navigating the legal complexities of real estate transactions, our
            team is here to assist you at every step. Explore our services below
            to see how we can help you make the most of your real estate
            journey.
          </HeadDescription>
        </HeadTextWrapper>
        <Image src={ServiceImg} alt="team image" />
      </HeadSection>
      <WhyUsSection>
        <WhyUsCard highlight>
          <CardIcon>01</CardIcon>
          <CardTitle>Property Search & Acquisition</CardTitle>
          <CardDescription>
            We specialize in helping clients find the perfect property that
            meets their unique needs, whether residential, commercial, or
            investment properties.
            <ul style={{marginTop:'0px'}}>
              <li>Residential Properties (Houses, Apartments, Villas)</li>
              <li>Commercial Spaces (Offices, Retail Units)</li>
              <li>Investment Opportunities (Land, Plots, Developments)</li>
              <li>Property Showings & Virtual Tours</li>
              <li>Comprehensive Market Analysis</li>
              <li>Negotiation Support for Best Deals</li>
            </ul>
          </CardDescription>
        </WhyUsCard>

        <WhyUsCard>
          <CardIcon>02</CardIcon>
          <CardTitle>Real Estate Brokerage Services</CardTitle>
          <CardDescription>
            Our experienced brokers ensure that you receive expert guidance
            throughout the buying and selling process.
            <ul style={{marginTop:'0px'}}>
              <li>Listing Your Property for Sale</li>
              <li>Buyer Representation Services</li>
              <li>Price Negotiation and Deal Structuring</li>
              <li>Marketing & Promotion of Properties</li>
              <li>Property Valuation Assistance</li>
              <li>Comparative Market Analysis Reports</li>
            </ul>
          </CardDescription>
        </WhyUsCard>

        <WhyUsCard highlight>
          <CardIcon>03</CardIcon>
          <CardTitle>Property Leasing & Rentals</CardTitle>
          <CardDescription>
            We assist both landlords and tenants in finding the right rental
            property or leasing agreement.
            <ul style={{marginTop:'0px'}}>
              <li>Residential Rentals (Houses, Apartments)</li>
              <li>Commercial Leasing (Offices, Retail Units, Warehouses)</li>
              <li>Tenant Representation Services</li>
              <li>Lease Agreement Negotiation</li>
              <li>Property Viewing & Consultation</li>
              <li>Long-term and Short-term Rental Management</li>
            </ul>
          </CardDescription>
        </WhyUsCard>

        <WhyUsCard>
          <CardIcon>04</CardIcon>
          <CardTitle>Legal Assistance & Documentation</CardTitle>
          <CardDescription>
            Navigating the legal aspects of real estate can be complex, but with
            our legal support services, you can proceed with confidence.
            <ul style={{marginTop:'0px'}}>
              <li>Contract Drafting and Review</li>
              <li>Property Transfer Services</li>
              <li>Title Search and Verification</li>
              <li>Mortgage and Loan Processing</li>
              <li>Power of Attorney Arrangements</li>
              <li>Compliance with Real Estate Regulations</li>
            </ul>
          </CardDescription>
        </WhyUsCard>

        <WhyUsCard highlight>
          <CardIcon>05</CardIcon>
          <CardTitle>Property Management</CardTitle>
          <CardDescription>
            Managing your property has never been easier with our expert
            property management services.
            <ul style={{marginTop:'0px'}}>
              <li>Tenant Screening & Onboarding</li>
              <li>Rent Collection & Disbursement</li>
              <li>Property Inspections & Maintenance</li>
              <li>Eviction Services (if necessary)</li>
              <li>Financial Reporting and Tax Assistance</li>
              <li>Renovation & Repair Management</li>
            </ul>
          </CardDescription>
        </WhyUsCard>

        <WhyUsCard>
          <CardIcon>06</CardIcon>
          <CardTitle>Real Estate Investment Advisory</CardTitle>
          <CardDescription>
            For clients interested in real estate investments, we provide
            tailored advisory services that help you maximize your returns.
            <ul style={{marginTop:'0px'}}>
              <li>Investment Property Search</li>
              <li>Risk Assessment & Feasibility Studies</li>
              <li>Portfolio Diversification Strategies</li>
              <li>Real Estate Market Trends and Forecasting</li>
              <li>Asset Management</li>
              <li>Financing and Capital Structuring</li>
            </ul>
          </CardDescription>
        </WhyUsCard>

        <WhyUsCard highlight>
          <CardIcon>07</CardIcon>
          <CardTitle>Project Development & Construction Advisory</CardTitle>
          <CardDescription>
            From planning to execution, we offer advisory services for real
            estate developers and construction projects.
            <ul style={{marginTop:'0px'}}>
              <li>Land Acquisition & Development Feasibility</li>
              <li>Site Planning and Design</li>
              <li>Vendor & Contractor Selection</li>
              <li>Construction Management Support</li>
              <li>Project Financing and Budgeting</li>
              <li>Risk Management & Permitting</li>
            </ul>
          </CardDescription>
        </WhyUsCard>

        <WhyUsCard>
          <CardIcon>08</CardIcon>
          <CardTitle>Relocation & Moving Services</CardTitle>
          <CardDescription>
            Relocating can be stressful, but we make the process easier with our complete relocation services.
            <ul style={{marginTop:'0px'}}>
              <li>Relocation Consulting and Planning</li>
              <li>Property Sourcing and Lease Negotiation</li>
              <li>Moving Coordination (Packing & Transport)</li>
              <li>Post-Move Support (Utilities, Local Services)</li>
              <li>Temporary Housing Arrangements</li>
              <li>Cross-border Relocation Support</li>
            </ul>
          </CardDescription>
        </WhyUsCard>

        <WhyUsCard highlight>
          <CardIcon>09</CardIcon>
          <CardTitle>Property Marketing Services</CardTitle>
          <CardDescription>
           We help property owners attract the right buyers or tenants through effective marketing strategies.
            <ul style={{marginTop:'0px'}}>
              <li>Professional Photography & Virtual Tours</li>
              <li>Online Property Listings</li>
              <li>Social Media Marketing Campaigns</li>
              <li>Email Marketing and Newsletters</li>
              <li>SEO for Property Listings</li>
              <li>Targeted Ad Campaigns</li>
            </ul>
          </CardDescription>
        </WhyUsCard>

        <WhyUsCard highlight={width > 468}>
          <CardIcon>10</CardIcon>
          <CardTitle>Mortgage & Financial Services</CardTitle>
          <CardDescription>
           Our financial experts assist clients with securing the best mortgage or loan options for their real estate investments.
            <ul style={{marginTop:'0px'}}>
              <li>Mortgage Pre-Approval Assistance</li>
              <li>Loan Comparison and Rate Analysis</li>
              <li>Refinancing Options</li>
              <li>Financial Planning for Real Estate Purchases</li>
              <li>Tax Optimization & Financial Advisory</li>
              <li>Liaising with Banks & Financial Institutions</li>
            </ul>
          </CardDescription>
        </WhyUsCard>


      </WhyUsSection>
      {/* <ServiceItems>
        <ServiceItem
          number="01"
          title="Property Search & Acquisition"
          description="We specialize in helping clients find the perfect property that meets their unique needs, whether residential, commercial, or investment properties."
          points={[
            "Residential Properties (Houses, Apartments, Villas)",
            "Commercial Spaces (Offices, Retail Units)",
            "Investment Opportunities (Land, Plots, Developments)",
            "Property Showings & Virtual Tours",
            "Comprehensive Market Analysis",
            "Negotiation Support for Best Deals",
          ]}
        />
        <ServiceItem
          number="02"
          title="Real Estate Brokerage Services"
          description="Our experienced brokers ensure that you receive expert guidance throughout the buying and selling process."
          points={[
            "Listing Your Property for Sale",
            "Buyer Representation Services",
            "Price Negotiation and Deal Structuring",
            "Marketing & Promotion of Properties",
            "Property Valuation Assistance",
            "Comparative Market Analysis Reports",
          ]}
        />
        <ServiceItem
          number="03"
          title="Property Leasing & Rentals"
          description="We assist both landlords and tenants in finding the right rental property or leasing agreement."
          points={[
            "Residential Rentals (Houses, Apartments)",
            "Commercial Leasing (Offices, Retail Units, Warehouses)",
            "Tenant Representation Services",
            "Lease Agreement Negotiation",
            "Property Viewing & Consultation",
            "Long-term and Short-term Rental Management",
          ]}
        />
        <ServiceItem
          number="04"
          title="Legal Assistance & Documentation"
          description="Navigating the legal aspects of real estate can be complex, but with our legal support services, you can proceed with confidence."
          points={[
            "Contract Drafting and Review",
            "Property Transfer Services",
            "Title Search and Verification",
            "Mortgage and Loan Processing",
            "Power of Attorney Arrangements",
            "Compliance with Real Estate Regulations",
          ]}
        />
        <ServiceItem
          number="05"
          title="Property Management"
          description="Managing your property has never been easier with our expert property management services."
          points={[
            "Tenant Screening & Onboarding",
            "Rent Collection & Disbursement",
            "Property Inspections & Maintenance",
            "Eviction Services (if necessary)",
            "Financial Reporting and Tax Assistance",
            "Renovation & Repair Management",
          ]}
        />
        <ServiceItem
          number="06"
          title="Real Estate Investment Advisory"
          description="For clients interested in real estate investments, we provide tailored advisory services that help you maximize your returns."
          points={[
            "Investment Property Search",
            "Risk Assessment & Feasibility Studies",
            "Portfolio Diversification Strategies",
            "Real Estate Market Trends and Forecasting",
            "Asset Management",
            "Financing and Capital Structuring",
          ]}
        />
        <ServiceItem
          number="07"
          title="Project Development & Construction Advisory"
          description="From planning to execution, we offer advisory services for real estate developers and construction projects."
          points={[
            "Land Acquisition & Development Feasibility",
            "Site Planning and Design",
            "Vendor & Contractor Selection",
            "Construction Management Support",
            "Project Financing and Budgeting",
            "Risk Management & Permitting",
          ]}
        />
        <ServiceItem
          number="08"
          title="Relocation & Moving Services"
          description="Relocating can be stressful, but we make the process easier with our complete relocation services."
          points={[
            "Relocation Consulting and Planning",
            "Property Sourcing and Lease Negotiation",
            "Moving Coordination (Packing & Transport)",
            "Post-Move Support (Utilities, Local Services)",
            "Temporary Housing Arrangements",
            "Cross-border Relocation Support",
          ]}
        />
        <ServiceItem
          number="09"
          title="Property Marketing Services"
          description="We help property owners attract the right buyers or tenants through effective marketing strategies."
          points={[
            "Professional Photography & Virtual Tours",
            "Online Property Listings",
            "Social Media Marketing Campaigns",
            "Email Marketing and Newsletters",
            "SEO for Property Listings",
            "Targeted Ad Campaigns",
          ]}
        />
        <ServiceItem
          number="10"
          title="Mortgage & Financial Services"
          description="Our financial experts assist clients with securing the best mortgage or loan options for their real estate investments."
          points={[
            "Mortgage Pre-Approval Assistance",
            "Loan Comparison and Rate Analysis",
            "Refinancing Options",
            "Financial Planning for Real Estate Purchases",
            "Tax Optimization & Financial Advisory",
            "Liaising with Banks & Financial Institutions",
          ]}
        />
      </ServiceItems> */}
    </PageWrapper>
  );
};

export default ServicesPage;
